import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { DatePicker, Form, Input, InputNumber, Modal, Row, Col, Select, Spin, Switch } from "antd";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import AmountInputNumber from "../../../../components/inputs/AmountInputNumber";

import {
  getDecaissementByCodeId,
  getParametre,
  updateDecaissement,
} from "../../../../hooks/useHooks";
import { PARAMETERS } from "../../../../variables/constants";

const EMPTY_SELECT_OPTIONS = [];
const REGLEMENT_NATURE_OPTIONS = [
  { label: "Sinistre en principal", value: "SINISTRE EN PRINCIPAL" },
  { label: "Frais accessoires", value: "ACCESSOIRES" },
  { label: "Honoraires", value: "HONORAIRES" },
  { label: "Provision", value: "PROVISION" },
];

const dedupeOptions = (options = []) => {
  const seen = new Set();
  return options.filter((option) => {
    if (!option || !option.value) {
      return false;
    }
    const key = option.value;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const sanitizeOption = (rawValue) => {
  if (rawValue === null || rawValue === undefined) {
    return null;
  }
  const value = `${rawValue}`.trim();
  if (!value || value.toLowerCase() === "ajout du parametre") {
    return null;
  }
  return { label: value, value };
};

const EditDecaissementModal = ({ codeId, open, onClose, onUpdated }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [decaissement, setDecaissement] = useState(null);
  const [motifs, setMotifs] = useState(EMPTY_SELECT_OPTIONS);
  const [motifsLoading, setMotifsLoading] = useState(false);
  const [bankOptions, setBankOptions] = useState(EMPTY_SELECT_OPTIONS);
  const [banksLoading, setBanksLoading] = useState(false);
  const [natureActeOptions, setNatureActeOptions] = useState(EMPTY_SELECT_OPTIONS);
  const [natureActeLoading, setNatureActeLoading] = useState(false);

  const beneficiaireLabel = useMemo(() => {
    if (!decaissement?.beneficiaire) {
      return "";
    }
    const benef = decaissement.beneficiaire;
    const nom = benef.nom || benef.libelle || benef?.personne?.nom || "";
    const prenom = benef.prenom || benef?.personne?.prenom || "";
    const raisonSociale = benef.raisonSociale || "";
    return [nom, prenom, raisonSociale].filter(Boolean).join(" ").trim();
  }, [decaissement]);

  const natureOptions = useMemo(() => {
    if (!decaissement?.nature) {
      return REGLEMENT_NATURE_OPTIONS;
    }
    const exists = REGLEMENT_NATURE_OPTIONS.some((option) => option.value === decaissement.nature);
    if (exists) {
      return REGLEMENT_NATURE_OPTIONS;
    }
    return [...REGLEMENT_NATURE_OPTIONS, { label: decaissement.nature, value: decaissement.nature }];
  }, [decaissement]);

  useEffect(() => {
    if (open) {
      setMotifsLoading(true);
      getParametre(PARAMETERS.motifReglement)
        .then(({ data }) => {
          const options = (data || []).map((motif) => ({
            label: motif.valeur,
            value: motif.valeur,
          }));
          setMotifs(options.length ? options : EMPTY_SELECT_OPTIONS);
        })
        .catch(() => {
          setMotifs(EMPTY_SELECT_OPTIONS);
        })
        .finally(() => setMotifsLoading(false));

      setBanksLoading(true);
      getParametre(PARAMETERS.banque)
        .then(({ data }) => {
          const bankList = dedupeOptions(
            (data || [])
              .map((bank) =>
                sanitizeOption(
                  bank?.libelleParametre ??
                    bank?.valeur ??
                    bank?.label ??
                    bank?.libelle ??
                    bank?.nom ??
                    bank?.value
                )
              )
              .filter(Boolean)
          );
          setBankOptions(bankList.length ? bankList : EMPTY_SELECT_OPTIONS);
        })
        .catch(() => {
          setBankOptions(EMPTY_SELECT_OPTIONS);
        })
        .finally(() => setBanksLoading(false));

      setNatureActeLoading(true);
      getParametre(PARAMETERS.natureActe)
        .then(({ data }) => {
          const natureList = dedupeOptions(
            (data || [])
              .map((nature) =>
                sanitizeOption(
                  nature?.libelleParametre ??
                    nature?.valeur ??
                    nature?.label ??
                    nature?.libelle ??
                    nature?.nom ??
                    nature?.value
                )
              )
              .filter(Boolean)
          );
          setNatureActeOptions(natureList.length ? natureList : EMPTY_SELECT_OPTIONS);
        })
        .catch(() => {
          setNatureActeOptions(EMPTY_SELECT_OPTIONS);
        })
        .finally(() => setNatureActeLoading(false));
    }
  }, [open]);

  useEffect(() => {
    if (decaissement?.banque) {
      setBankOptions((prev) => {
        const option = sanitizeOption(decaissement.banque);
        if (!option) {
          return prev.length ? prev : EMPTY_SELECT_OPTIONS;
        }
        return dedupeOptions([...(prev || []), option]);
      });
    }
  }, [decaissement]);

  useEffect(() => {
    if (decaissement?.natureActe) {
      setNatureActeOptions((prev) => {
        const option = sanitizeOption(decaissement.natureActe);
        if (!option) {
          return prev.length ? prev : EMPTY_SELECT_OPTIONS;
        }
        return dedupeOptions([...(prev || []), option]);
      });
    }
  }, [decaissement]);

  useEffect(() => {
    if (open && codeId) {
      setLoading(true);
      getDecaissementByCodeId(codeId)
        .then(({ data }) => {
          setDecaissement(data);
          form.setFieldsValue({
            montantGlobal: data?.montantGlobal ?? 0,
            taxes: data?.taxes ?? 0,
            taux: data?.taux ?? 0,
            motifReglement: data?.motifReglement ?? "",
            formuleDeFin: data?.formuleDeFin ?? "",
            nature: data?.nature ?? undefined,
            natureActe: data?.natureActe ?? undefined,
            encaissement: Boolean(data?.encaissement),
            expertise: Boolean(data?.expertise),
            numeroCheque: data?.numeroCheque ?? "",
            banque: data?.banque ?? undefined,
            representant: data?.representant ?? "",
            commentaire: data?.commentaire ?? data?.observations ?? "",
            dateReglement: data?.dateReglement ? dayjs(data.dateReglement) : null,
          });
        })
        .catch(() => {
          toast.error("Impossible de charger le décaissement sélectionné");
          handleClose();
        })
        .finally(() => setLoading(false));
    }

    if (!open) {
      form.resetFields();
      setDecaissement(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, codeId]);

  const handleClose = () => {
    form.resetFields();
    setDecaissement(null);
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!decaissement) {
      return;
    }

    try {
      const values = await form.validateFields();
      setSaving(true);
      const normalizeNumber = (val, fallback = 0) => {
        if (val === null || val === undefined || val === "") {
          return fallback;
        }
        const numeric = Number(val);
        return Number.isNaN(numeric) ? fallback : numeric;
      };
      const payload = {
        ...decaissement,
        montantGlobal: normalizeNumber(values.montantGlobal),
        taxes: normalizeNumber(values.taxes),
        taux: normalizeNumber(values.taux),
        motifReglement: values.motifReglement ?? "",
        formuleDeFin: values.formuleDeFin ?? "",
        nature: values.nature ?? "",
        natureActe: values.natureActe ?? "",
        encaissement: values.encaissement ?? false,
        expertise: values.expertise ?? false,
        numeroCheque: values.numeroCheque || null,
        banque: values.banque || null,
        representant: values.representant || null,
        commentaire: values.commentaire || null,
        observations: values.commentaire || null,
        dateReglement: values.dateReglement
          ? values.dateReglement.toISOString()
          : null,
      };

      const response = await updateDecaissement(payload);
      toast.success("Décaissement mis à jour avec succès");
      setSaving(false);
      handleClose();
      if (onUpdated) {
        onUpdated(response?.data || payload);
      } else {
        window.location.reload();
      }
    } catch (error) {
      setSaving(false);
      if (error?.errorFields) {
        return;
      }
      toast.error("Une erreur est survenue lors de la mise à jour");
    }
  };

  const handleMotifChange = (value) => {
    if (value) {
      form.setFieldsValue({
        formuleDeFin: value,
        motifReglement: value,
      });
    } else {
      form.setFieldsValue({ formuleDeFin: undefined });
    }
  };

  return (
    <Modal
      title="Éditer le règlement"
      open={open}
      okText="Enregistrer"
      cancelText="Annuler"
      confirmLoading={saving}
      width={920}
      zIndex={2000}
      onOk={handleSubmit}
      onCancel={handleClose}
      destroyOnClose
    >
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
          <Spin />
        </div>
      ) : (
        <Form
          layout="vertical"
          form={form}
          requiredMark={false}
        >
          {beneficiaireLabel && (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label="Bénéficiaire">
                  <Input value={beneficiaireLabel} disabled />
                </Form.Item>
              </Col>
            </Row>
          )}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Montant TTC (FCFA)"
                name="montantGlobal"
                rules={[{ required: true, message: "Veuillez renseigner le montant" }]}
              >
                <AmountInputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Montant retenu / Taxes (FCFA)" name="taxes">
                <AmountInputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Taux" name="taux">
                <InputNumber min={0} max={100} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Nature" name="nature">
                <Select
                  allowClear
                  placeholder="Sélectionner la nature"
                  options={natureOptions}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Motif (sélection)" name="formuleDeFin">
                <Select
                  allowClear
                  loading={motifsLoading}
                  onChange={handleMotifChange}
                  options={motifs}
                  placeholder="Sélectionner un motif"
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Date de règlement" name="dateReglement">
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Motif (description)" name="motifReglement">
                <Input.TextArea rows={5} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Numéro de chèque" name="numeroCheque">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Banque" name="banque">
                <Select
                  allowClear
                  loading={banksLoading}
                  placeholder="Sélectionner la banque"
                  options={bankOptions}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Représentant" name="representant">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Commentaire / Partie adverse" name="commentaire">
                <Input.TextArea rows={4} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Décaissement encaissé"
                name="encaissement"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Expertise" name="expertise" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      )}
    </Modal>
  );
};

EditDecaissementModal.propTypes = {
  codeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onUpdated: PropTypes.func,
};

export default EditDecaissementModal;
